import { useI18n } from "@core/i18n/i18n";
import { useNavigate } from "@core/routing/use-navigate";
import { templateManager } from "@core/service-container";
import { PatientStatus } from "@domain/patient/patient";
import { getProgramDetails } from "@domain/program/program";
import { sendAnalyticsEventUseTemplateForProgram } from "@domain/program/program-analytics";
import { TemplateItem } from "@domain/template/template";
import { useUser } from "@domain/user/hooks/use-user";
import { e2eTestId } from "@e2e/test-ids";
import { Button } from "@ui/components/buttons/button";
import { TertiaryButton } from "@ui/components/buttons/tertiary-button";
import { DialogContext, ModalId } from "@ui/components/dialog/dialog-provider";
import { RoutesPath } from "@ui/navigation/routes";
import { isNotNil } from "@utils/assert";
import { SplitButton } from "primereact/splitbutton";
import React, { useCallback, useContext } from "react";

export enum CreateProgramButtonType {
	FromTemplate = "fromTemplate",
	New = "new",
	NewOrFromTemplate = "newOrFromTemplate",
}

export const CreateProgramButton: React.FC<{
	patient: { id: string; analyticsId: string; status?: PatientStatus };
	buttonType: CreateProgramButtonType;
	onClick?: (fromTemplate: boolean) => void;
}> = ({ patient, buttonType, onClick }) => {
	const { format } = useI18n();
	const user = useUser();
	const navigate = useNavigate();
	const dialogContext = useContext(DialogContext);
	const onButtonClick = useCallback(() => {
		onClick?.(false);
		navigate(RoutesPath.ProgramBuilder, { route: { patientId: patient.id } });
	}, [navigate, onClick, patient.id]);

	const onTemplateButtonClicked = useCallback(() => {
		onClick?.(true);
		isNotNil(user) &&
			dialogContext?.openModal(ModalId.TemplateListing, {
				editor: user,
				onTemplateClicked: async (template: TemplateItem) => {
					const detailedTemplate = await templateManager.fetchTemplate(template.id);
					const exerciseDetails = getProgramDetails(detailedTemplate.days);
					sendAnalyticsEventUseTemplateForProgram(patient.analyticsId, template, exerciseDetails, patient.status);
					navigate(RoutesPath.ProgramBuilder, {
						route: { patientId: patient.id },
						query: { templateId: template.id },
					});
				},
			});
	}, [dialogContext, navigate, onClick, patient, user]);

	switch (buttonType) {
		case CreateProgramButtonType.NewOrFromTemplate:
			return (
				<SplitButton
					id={e2eTestId.patientPage.createProgramButton}
					label={format("patientDetails.patientPrograms.createProgramButton")}
					onClick={onButtonClick}
					model={[
						{
							label: format("patientDetails.patientPrograms.createButtonStartFromTemplate"),
							command: onTemplateButtonClicked,
						},
					]}
				/>
			);
		case CreateProgramButtonType.FromTemplate:
			return (
				<TertiaryButton
					onClick={onTemplateButtonClicked}
					label={format("patientDetails.patientPrograms.createButtonStartFromTemplate")}
				></TertiaryButton>
			);
		case CreateProgramButtonType.New:
			return (
				<Button
					id={e2eTestId.patientPage.createProgramButton}
					label={format("patientDetails.patientPrograms.createProgramButton")}
					onClick={onButtonClick}
					style={{ minHeight: "50px" }}
				/>
			);
	}
};
